import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';
import { getApiErrorMessage } from '../utils/formErrors';
import {
  clearPaymentAttemptSessionId,
  getStoredPaymentAttemptSessionId,
} from '../utils/paymentAttemptApi';
import {
  PAYMENT_PROOF_ACCEPT,
  PAYMENT_PROOF_HINT,
  PAYMENT_PROOF_REJECT_MESSAGE,
  isAllowedPaymentProofFile,
  isPaymentProofImage,
} from '../utils/paymentProofFile';

const PLAN_META = {
  emi: {
    title: 'Monthly EMI Plan',
    amount: '₹500',
    accent: 'from-violet-500 to-indigo-500',
    shadow: 'shadow-indigo-300/60',
  },
  full: {
    title: 'Full Settlement in 1st Month',
    amount: '₹5,000',
    accent: 'from-cyan-500 to-teal-500',
    shadow: 'shadow-cyan-300/60',
  },
};

/** API `plan_type` values for POST /api/v2/plans/upload-screenshot/ */
const PLAN_TYPE_API = {
  emi: 'monthly_emi',
  full: 'full_settlement',
};

const UPLOAD_FAIL_FALLBACK = 'Failed to upload the payment screenshot.';
const SNACK_DURATION_MS = { success: 1800, warning: 1800, error: 5000 };

function PaymentProofUpload() {
  const location = useLocation();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const selectedPlanId = location.state?.selectedPlan || 'emi';
  const plan = PLAN_META[selectedPlanId] || PLAN_META.emi;
  const planTitle = location.state?.planName || plan.title;
  const displayAmount =
    location.state?.amountLabel || plan.amount;
  const payReminder =
    selectedPlanId === 'full'
      ? 'Your proof must show a single payment of ₹5,000 (full settlement). A ₹500 payment belongs to Monthly EMI.'
      : 'Your proof must show a payment of ₹500 for this month\'s EMI. Do not upload a ₹5,000 receipt unless you chose Full Settlement.';

  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [emailVerifyBlock, setEmailVerifyBlock] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const fileInputRef = useRef(null);
  const timeoutRef = useRef(null);
  const previewUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const showSnack = (message, type = 'success') => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setSnack({ message, type });
    timeoutRef.current = setTimeout(
      () => setSnack(null),
      SNACK_DURATION_MS[type] || SNACK_DURATION_MS.success
    );
  };

  const onFileSelect = (file) => {
    if (!file) return;
    if (!isAllowedPaymentProofFile(file)) {
      showSnack(PAYMENT_PROOF_REJECT_MESSAGE, 'error');
      return;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setSelectedFile(file);
    showSnack('File selected successfully.');
  };

  const removeSelectedFile = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setSelectedFile(null);
    showSnack('Selected file removed.', 'warning');
  };

  const onDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const droppedFile = event.dataTransfer.files?.[0];
    onFileSelect(droppedFile);
  };

  const onUpload = async () => {
    if (!selectedFile) {
      showSnack('Please select a file before uploading.', 'warning');
      return;
    }
    if (isUploading) return;

    const formData = new FormData();
    formData.append('screenshot', selectedFile);
    formData.append('plan_type', PLAN_TYPE_API[selectedPlanId] || PLAN_TYPE_API.emi);
    const attemptSessionId = getStoredPaymentAttemptSessionId();
    if (attemptSessionId) {
      formData.append('payment_attempt_session_id', attemptSessionId);
    }

    setIsUploading(true);
    setEmailVerifyBlock('');
    try {
      const response = await api.post(API.UPLOAD_PLAN_SCREENSHOT, formData);

      if (response.data?.success) {
        clearPaymentAttemptSessionId();
        try {
          const prof = await api.get(API.PROFILE);
          updateUser(prof.data);
        } catch {
          /* profile refresh is best-effort; status page still loads */
        }
        navigate('/payment/status', { state: { selectedPlan: selectedPlanId } });
        return;
      }

      showSnack(getApiErrorMessage(response.data, UPLOAD_FAIL_FALLBACK), 'error');
    } catch (err) {
      const data = err.response?.data;
      const msg = getApiErrorMessage(data, UPLOAD_FAIL_FALLBACK);
      const needsEmail =
        data?.code === 'email_not_verified' || /verify your email/i.test(msg);
      if (needsEmail) {
        setEmailVerifyBlock(msg);
      }
      showSnack(msg, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      const res = await api.post(API.RESEND_VERIFICATION);
      showSnack(res.data.message || 'Verification email sent.', 'success');
      setEmailVerifyBlock('');
    } catch (err) {
      showSnack(getApiErrorMessage(err.response?.data, 'Could not resend verification email.'), 'error');
    } finally {
      setResendLoading(false);
    }
  };

  const isImageFile = isPaymentProofImage(selectedFile);
  const previewUrl =
    isImageFile && selectedFile
      ? (() => {
          if (previewUrlRef.current) return previewUrlRef.current;
          previewUrlRef.current = URL.createObjectURL(selectedFile);
          return previewUrlRef.current;
        })()
      : null;

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] overflow-hidden bg-gradient-to-br from-slate-100 via-indigo-50 to-cyan-100 px-4 py-3 sm:px-5 sm:py-4 flex items-center">
      <div className="max-w-4xl mx-auto w-full">
        <div className="h-full rounded-3xl bg-white/90 backdrop-blur border border-white p-5 sm:p-6 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.35)] flex flex-col">
          <p className="text-xs font-bold tracking-[0.22em] text-indigo-600 uppercase mb-2">
            Upload Payment Proof
          </p>
          <h1
            className="text-2xl sm:text-3xl font-black text-slate-900 mb-2"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Submit Screenshot or PDF
          </h1>
          <p className="text-sm text-slate-600 mb-3">
            Plan: <strong>{planTitle}</strong>
          </p>
          <div
            className={`mb-4 rounded-xl border px-4 py-3 ${
              selectedPlanId === 'full'
                ? 'border-cyan-200 bg-cyan-50/80'
                : 'border-violet-200 bg-violet-50/80'
            }`}
          >
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Expected amount on proof
            </p>
            <p className="text-2xl font-black text-slate-900">{displayAmount}</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-700">{payReminder}</p>
          </div>

          {emailVerifyBlock && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-900 mb-2">{emailVerifyBlock}</p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
              >
                {resendLoading ? 'Sending…' : 'Resend verification email'}
              </button>
            </div>
          )}

          <div
            onDrop={onDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragActive(true);
            }}
            onDragLeave={() => setIsDragActive(false)}
            className={`rounded-2xl border-2 border-dashed h-48 sm:h-52 px-6 text-center transition-all flex flex-col items-center justify-center ${
              isDragActive
                ? 'border-indigo-400 bg-indigo-50'
                : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
            }`}
          >
            <p className="text-sm sm:text-base font-semibold text-slate-700 mb-2">
              Drag & drop screenshot / PDF here
            </p>
            <p className="text-xs text-slate-500 mb-5">Supported: {PAYMENT_PROOF_HINT}</p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-xl px-5 py-2.5 text-sm font-bold bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
            >
              Choose File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={PAYMENT_PROOF_ACCEPT}
              className="hidden"
              onChange={(e) => onFileSelect(e.target.files?.[0])}
            />
          </div>

          {selectedFile && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3 relative">
              <button
                type="button"
                onClick={removeSelectedFile}
                className="absolute top-2 right-2 h-7 w-7 rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 text-sm font-bold"
                aria-label="Remove selected file"
              >
                ×
              </button>
              <p className="text-xs text-slate-500 mb-2">Selected file</p>
              <div className="flex items-center gap-3">
                {isImageFile ? (
                  <img
                    src={previewUrl}
                    alt="Payment proof preview"
                    className="h-14 w-14 rounded-lg object-cover border border-slate-200"
                  />
                ) : (
                  <div className="h-14 w-14 rounded-lg border border-slate-200 bg-slate-100 flex items-center justify-center text-xl">
                    📄
                  </div>
                )}
                <p className="text-sm font-semibold text-slate-800 pr-8 break-all">
                  {selectedFile.name}
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onUpload}
            disabled={!selectedFile || isUploading}
            className={`mt-4 w-full rounded-xl px-5 py-3 text-sm font-extrabold text-white bg-gradient-to-r ${plan.accent} shadow-lg ${plan.shadow} transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isUploading ? 'Uploading…' : 'Upload Payment Proof'}
          </button>
        </div>
      </div>

      {snack && (
        <div
          className={`fixed bottom-6 right-6 z-50 rounded-xl px-4 py-3 text-sm font-semibold shadow-xl max-w-sm ${
            snack.type === 'warning'
              ? 'bg-amber-100 text-amber-900 border border-amber-300'
              : snack.type === 'error'
                ? 'bg-red-100 text-red-900 border border-red-300'
                : 'bg-emerald-100 text-emerald-900 border border-emerald-300'
          }`}
          role="alert"
        >
          {snack.message}
        </div>
      )}
    </div>
  );
}

export default PaymentProofUpload;
